import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTicketByToken, useAddPublicComment } from "@/hooks/useTickets";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { PriorityBadge } from "@/components/ui/priority-badge";
import { StageBadge } from "@/components/ui/stage-badge";
import { DaysSinceSubmissionBadge, getDaysSinceSubmission } from "@/components/ui/days-since-badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Loader2,
  Clock,
  User,
  Mail,
  Phone,
  Building,
  Calendar,
  Paperclip,
  Send,
  FileText,
  MessageSquare,
  Search,
} from "lucide-react";
import { format, isValid } from "date-fns";
import { toast } from "sonner";
import { getErrorMessage, getAttachmentViewUrl, getAttachmentFileName } from "@/lib/utils";

export function TrackTicketPage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [searchToken, setSearchToken] = useState("");
  const { data: ticket, isLoading, error } = useTicketByToken(token ?? null);
  const addComment = useAddPublicComment();
  const [comment, setComment] = useState("");
  const [files, setFiles] = useState<File[]>([]);

  const handleSearch = () => {
    if (searchToken.trim()) {
      navigate(`/track/${searchToken.trim()}`);
    }
  };

  const handleAddComment = async () => {
    if (!comment.trim() || !ticket) return;

    try {
      await addComment.mutateAsync({
        token: token!,
        content: comment,
        requesterEmail: ticket.requesterEmail ?? ticket.requester_email,
      });

      // Note: File uploads would be handled by the backend API
      if (files.length > 0) {
        console.log(
          "Files selected but upload not implemented yet:",
          files.map((f) => f.name),
        );
        // TODO: Implement file upload via API endpoint
      }

      setComment("");
      setFiles([]);
      toast.success("Reply sent successfully");
    } catch (error: unknown) {
      toast.error(getErrorMessage(error, "Failed to send reply"));
    }
  };

  // Search form when no token provided
  if (!token) {
    return (
      <div className="max-w-xl mx-auto animate-fade-in">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Track Your Ticket
          </h1>
          <p className="text-muted-foreground">
            Enter your tracking token to view your ticket status and updates.
          </p>
        </div>

        <Card>
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <Input
                placeholder="Enter your tracking token..."
                value={searchToken}
                onChange={(e) => setSearchToken(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              />
              <Button onClick={handleSearch}>
                <Search className="h-4 w-4 mr-2" />
                Track
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="max-w-xl mx-auto text-center">
        <Card>
          <CardContent className="pt-6">
            <h2 className="text-xl font-semibold mb-2">Ticket Not Found</h2>
            <p className="text-muted-foreground mb-4">
              We couldn't find a ticket with that tracking token. Please check
              the link and try again.
            </p>
            <Button variant="outline" onClick={() => navigate("/track")}>
              Try Another Token
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto animate-fade-in space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            <h1 className="text-2xl font-bold text-foreground">
              {ticket.ticketNumber}
            </h1>
            <DaysSinceSubmissionBadge days={getDaysSinceSubmission(ticket)} />
            {ticket.stage && <StageBadge stage={ticket.stage} />}
          </div>
          <p className="text-muted-foreground">{ticket.ticketType?.name}</p>
        </div>
        <PriorityBadge priority={ticket.priority} />
        <Button variant="outline" onClick={() => navigate("/track")}>
          Back to Search
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        <div className="md:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="whitespace-pre-wrap">{ticket.description}</p>
              {ticket.expectedOutcome && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-medium mb-2">Expected Outcome</h4>
                  <p className="text-muted-foreground">
                    {ticket.expectedOutcome}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {ticket.attachments && ticket.attachments.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Paperclip className="h-5 w-5" />
                  Attachments
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {ticket.attachments.map((attachment) => {
                    const att = attachment as Record<string, unknown>;
                    const viewUrl = getAttachmentViewUrl(att);
                    const name = getAttachmentFileName(att);
                    const canOpen = viewUrl && viewUrl.length > 0;
                    return canOpen ? (
                      <a
                        key={attachment.id}
                        href={viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-3 rounded-lg border hover:bg-muted/50 transition-colors"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm truncate">{name}</span>
                      </a>
                    ) : (
                      <div
                        key={attachment.id}
                        className="flex items-center gap-2 p-3 rounded-lg border bg-muted/30"
                      >
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <span className="text-sm truncate">{name}</span>
                        <span className="text-xs text-muted-foreground ml-auto">Unavailable</span>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Comments/Updates */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <MessageSquare className="h-4 w-4" />
                Updates & Replies
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {ticket.comments && ticket.comments.length > 0 ? (
                <div className="space-y-4">
                  {ticket.comments
                    .filter(
                      (c) => !(c.is_internal ?? c.isInternal)
                    )
                    .map((comment) => {
                      const commentDate =
                        comment.created_at ?? comment.createdAt;
                      const date = commentDate ? new Date(commentDate) : null;
                      const formattedDate =
                        date && isValid(date)
                          ? format(date, "MMM d, yyyy h:mm a")
                          : "—";
                      return (
                        <div
                          key={comment.id}
                          className={`p-4 rounded-lg ${
                            comment.is_from_requester ?? comment.isFromRequester
                              ? "bg-primary/5 border border-primary/20"
                              : "bg-muted"
                          }`}
                        >
                          <div className="flex items-center gap-2 mb-2">
                            <Avatar className="h-6 w-6">
                              <AvatarFallback>
                                {comment.is_from_requester ??
                                comment.isFromRequester
                                  ? "Y"
                                  : "T"}
                              </AvatarFallback>
                            </Avatar>
                            <span className="text-sm font-medium">
                              {comment.is_from_requester ??
                              comment.isFromRequester
                                ? "You"
                                : "Tech Team"}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {formattedDate}
                            </span>
                          </div>
                          <p className="text-sm">{comment.content}</p>
                          {((comment as Record<string, unknown>).attachments as Array<Record<string, unknown>> | undefined)?.length ? (
                            <div className="mt-3 pt-3 border-t border-border/50 flex flex-wrap gap-2">
                              {((comment as Record<string, unknown>).attachments as Array<Record<string, unknown>>).map((att) => {
                                const url = getAttachmentViewUrl(att);
                                const fileName = getAttachmentFileName(att);
                                return url ? (
                                  <a
                                    key={String(att.id)}
                                    href={url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-1.5 px-2 py-1.5 rounded border text-xs hover:bg-muted/50"
                                  >
                                    <FileText className="h-3 w-3 shrink-0" />
                                    <span className="truncate max-w-[140px]">{fileName}</span>
                                  </a>
                                ) : (
                                  <span key={String(att.id)} className="flex items-center gap-1.5 px-2 py-1.5 rounded border text-xs text-muted-foreground">
                                    <FileText className="h-3 w-3 shrink-0" />
                                    <span className="truncate max-w-[140px]">{fileName}</span>
                                  </span>
                                );
                              })}
                            </div>
                          ) : null}
                        </div>
                      );
                    })}
                </div>
              ) : (
                <p className="text-muted-foreground text-center py-4">
                  No updates yet. The tech team will respond soon.
                </p>
              )}

              <Separator />

              {/* Reply Form */}
              <div className="space-y-3">
                <Textarea
                  placeholder="Add a reply or provide more information..."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  className="min-h-[100px]"
                />
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                    <Paperclip className="h-4 w-4" />
                    Attach files
                    <input
                      type="file"
                      multiple
                      className="hidden"
                      onChange={(e) =>
                        setFiles(Array.from(e.target.files ?? []))
                      }
                    />
                  </label>
                  <Button
                    onClick={handleAddComment}
                    disabled={!comment.trim() || addComment.isPending}
                  >
                    {addComment.isPending ? (
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Send Reply
                  </Button>
                </div>
                {files.length > 0 && (
                  <div className="text-sm text-muted-foreground">
                    {files.length} file(s) selected
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Ticket Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Ticket Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 text-sm">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Created</p>
                  <p className="font-medium">
                    {(() => {
                      const dateStr =
                        ticket.created_at ?? ticket.createdAt;
                      const d = dateStr ? new Date(dateStr) : null;
                      return d && isValid(d)
                        ? format(d, "MMM d, yyyy h:mm a")
                        : "—";
                    })()}
                  </p>
                </div>
              </div>
              {(() => {
                const val = ticket.estimatedValue ?? (ticket as { estimated_value?: number }).estimated_value;
                if (val == null || val === 0) return null;
                const type = ticket.estimatedType ?? (ticket as { estimation_type?: string }).estimation_type ?? "";
                return (
                  <div className="flex items-center gap-3 text-sm">
                    <Clock className="h-4 w-4 text-muted-foreground" />
                    <div>
                      <p className="text-muted-foreground">Estimation Time</p>
                      <p className="font-medium">
                        {val} {type}
                      </p>
                    </div>
                  </div>
                );
              })()}
              {ticket.dueDate && (
                <div className="flex items-center gap-3 text-sm">
                  <Calendar className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Due Date</p>
                    <p className="font-medium">
                      {format(new Date(ticket.dueDate), "MMM d, yyyy")}
                    </p>
                  </div>
                </div>
              )}
              <div className="flex items-center gap-3 text-sm">
                <Building className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-muted-foreground">Department</p>
                  <p className="font-medium">{ticket.department?.name}</p>
                </div>
              </div>
              {ticket.relatedSystem && (
                <div className="flex items-center gap-3">
                  <FileText className="h-4 w-4 text-muted-foreground" />
                  <div>
                    <p className="text-muted-foreground">Related System</p>
                    <p className="font-medium capitalize">
                      {ticket.relatedSystem}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Assigned Team - support both public API shape { name } and admin shape { user: { profile } } */}
          {Array.isArray(ticket.assignees) && ticket.assignees.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Assigned Team</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {ticket.assignees
                    .filter((a) => a != null && (a.user != null || (a as { name?: string }).name != null))
                    .map((assignee, idx) => {
                      const profile = assignee?.user?.profile;
                      const name =
                        profile?.name ??
                        (profile as { name?: string } | undefined)?.name ??
                        (assignee as { name?: string }).name ??
                        "Tech Team";
                      const avatarUrl = profile?.avatarUrl ?? (profile as { avatar_url?: string } | undefined)?.avatar_url;
                      const position = profile?.position ?? (profile as { position?: string } | undefined)?.position ?? "Tech Team";
                      return (
                        <div key={(assignee as { id?: string }).id ?? `assignee-${idx}`} className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <AvatarImage src={avatarUrl ?? undefined} />
                            <AvatarFallback>
                              {name?.charAt(0) ?? "U"}
                            </AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="text-sm font-medium">{name}</p>
                            <p className="text-xs text-muted-foreground">{position}</p>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Contact Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Your Contact Info</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <span>
                  {ticket.requesterName ?? ticket.requester_name ?? "—"}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>
                  {ticket.requesterEmail ?? ticket.requester_email ?? "—"}
                </span>
              </div>
              {(ticket.requesterPhone ?? ticket.requester_phone) && (
                <div className="flex items-center gap-2">
                  <Phone className="h-4 w-4 text-muted-foreground" />
                  <span>
                    {ticket.requesterPhone ?? ticket.requester_phone}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
